/* eslint-disable import/no-unresolved */
import {
    DollarCircleOutlined,
    RightOutlined,
    ShoppingCartOutlined,
} from '@ant-design/icons';
import { Form, InputNumber, Tooltip } from 'antd';
import { ApartmentIcon, CartButton } from 'components/UI/ProductCard/atoms';
import { ProductCardListProps } from 'components/UI/ProductCard/organisms/ProductCardList/types';
import { INVALID_QUANTITY_MESSAGE } from 'constants/Global/QuantityValidation.const';
import { PackingTypeSale } from 'enums/packingTypeSale';
import { useCallback, useEffect, useMemo, useState } from 'react';

import TrolleyLimitModal from 'components/Modals/TrolleyLimitModal';
import { usePurchaseInfoDisplay } from 'hooks/usePurchaseInfoDisplay';
import { useShippingFee } from 'hooks/useShippingFee';
import InfoDirected from 'screens/Products/components/ProductItem/components/InfoDirected';
import { initialPackingSelectedState } from 'store/state/gfpOffer/gfpOfferStore';
import { formatDecimal, multiplyMoney } from 'utils/number';
import { calculateMinBySaleType } from 'utils/util';
import * as S from './ProductCardActions.styled';

export interface ProductCardActionsProps
  extends Pick<
    ProductCardListProps,
    'product' | 'packingSelected' | 'disabled'
  > {
  onAddToCart: (
    quantity: number,
    saleType: PackingTypeSale,
  ) => void | Promise<void>;
  onBuyNow: (
    quantity: number,
    saleType: PackingTypeSale,
  ) => void | Promise<void>;
  onShowDetails: () => void;
  onComposition?: () => void;
  showCompositionButton?: boolean;
  showCartText?: boolean;
  disableBuyButton?: boolean;
}

type SaleOption = {
  label: string;
  value: PackingTypeSale;
  price: number;
  available: boolean;
};

export function ProductCardActions({
  product,
  packingSelected,
  disabled = false,
  onAddToCart,
  onBuyNow,
  onShowDetails,
  onComposition,
  showCompositionButton = false,
  showCartText = true,
  disableBuyButton = false,
}: ProductCardActionsProps) {
  const [selectedTypeSale, setSelectedTypeSale] = useState<PackingTypeSale>(
    PackingTypeSale.Packing,
  );
  const [form] = Form.useForm();
  const [quantity, setQuantity] = useState<number | null>(null);
  const [showTrolleyModal, setShowTrolleyModal] = useState(false);
  const [pendingAction, setPendingAction] = useState<'addToCart' | 'buyNow'>('addToCart');
  const { shippingFee, hasShippingFee } = useShippingFee(product);
  const parsedQuantity = useMemo(
    () => Number(quantity || 0),
    [quantity],
  );
  const isQuantityValid = useMemo(() => parsedQuantity > 0, [parsedQuantity]);

  const getTextInputTypeSale = useCallback(() => {
    switch (selectedTypeSale) {
      case PackingTypeSale.Packing:
        return 'Embalagem(s)';
      case PackingTypeSale.Layer:
        return 'Camada(s)';
      case PackingTypeSale.Trolley:
        return 'Carrinho(s)';
      default:
        return 'Embalagem(s)';
    }
  }, [selectedTypeSale]);

  const getLabel = useCallback(() => {
    if (selectedTypeSale === PackingTypeSale.Packing) {
      return packingSelected?.maxAvailablePackingsLabel || '';
    }
    if (selectedTypeSale === PackingTypeSale.Layer) {
      return packingSelected?.maxAvailableLayersLabel || '';
    }
    return packingSelected?.maxAvailableTrolleysLabel || '';
  }, [
    selectedTypeSale,
    packingSelected?.maxAvailableTrolleysLabel,
    packingSelected?.maxAvailableLayersLabel,
    packingSelected?.maxAvailablePackingsLabel,
  ]);

  const getMultipleCalc = useCallback(() => {
    if (!packingSelected?.multiple) return 1;
    if (selectedTypeSale === PackingTypeSale.Packing) {
      return (
        packingSelected?.multiple / +packingSelected?.maxAvailablePackingsLabel
      );
    }
    if (selectedTypeSale === PackingTypeSale.Layer) {
      const values = packingSelected?.maxAvailableLayersLabel
        ?.split('x')
        .map((x) => +x);
      if (!values || values.length === 0) return 1;
      const calc = packingSelected?.multiple / values.reduce((a, b) => +a * +b);
      return calc < 1 ? 1 : calc;
    }
    const values = packingSelected?.maxAvailableTrolleysLabel
      ?.split('x')
      .map((x) => +x);
    if (!values || values.length === 0) return 1;
    const calc = packingSelected?.multiple / values.reduce((a, b) => +a * +b);
    return calc < 1 ? 1 : calc;
  }, [
    selectedTypeSale,
    packingSelected?.multiple,
    packingSelected?.maxAvailableTrolleysLabel,
    packingSelected?.maxAvailableLayersLabel,
    packingSelected?.maxAvailablePackingsLabel,
  ]);

  const getErrorMinMessage = useCallback(() => {
    if (!packingSelected?.minimumQuantity) return '';
    const textInputTypeSale = getTextInputTypeSale();
    if (selectedTypeSale === PackingTypeSale.Packing) {
      return `Quantidade mínima: ${
        packingSelected?.minimumQuantity /
        +packingSelected?.maxAvailablePackingsLabel
      } ${textInputTypeSale}`;
    }
    if (selectedTypeSale === PackingTypeSale.Layer) {
      const values = packingSelected?.maxAvailableLayersLabel
        ?.split('x')
        .map((x) => +x);
      if (!values || values.length === 0) return '';
      const calc =
        packingSelected?.minimumQuantity / values.reduce((a, b) => +a * +b);
      return `Quantidade mínima: ${calc < 1 ? 1 : calc} ${textInputTypeSale}`;
    }
    const values = packingSelected?.maxAvailableTrolleysLabel
      ?.split('x')
      .map((x) => +x);
    if (!values || values.length === 0) return '';
    const calc =
      packingSelected?.minimumQuantity / values.reduce((a, b) => +a * +b);
    return `Quantidade mínima: ${calc < 1 ? 1 : calc} ${textInputTypeSale}`;
  }, [
    selectedTypeSale,
    packingSelected?.maxAvailableTrolleysLabel,
    packingSelected?.minimumQuantity,
    packingSelected?.maxAvailablePackingsLabel,
    packingSelected?.maxAvailableLayersLabel,
    getTextInputTypeSale,
  ]);

  const validateMaximumQuantity = useCallback(
    (_: unknown, value: string) => {
      if (!value) return Promise.resolve();

      const numValue = parseInt(value, 10);

      let maxBySaleType = 0;

      switch (selectedTypeSale) {
        case PackingTypeSale.Layer:
          maxBySaleType = packingSelected?.maxAvailableLayersQuantity || 0;
          break;

        case PackingTypeSale.Trolley:
          maxBySaleType = packingSelected?.maxAvailableTrolleysQuantity || 0;
          break;

        default:
          maxBySaleType = packingSelected?.maxAvailablePackingsQuantity || 0;
      }

      if (numValue > maxBySaleType) {
        return Promise.reject(
          new Error(
            `Quantidade máxima: ${maxBySaleType} ${getTextInputTypeSale()}`,
          ),
        );
      }

      return Promise.resolve();
    },
    [
      selectedTypeSale,
      packingSelected?.maxAvailablePackingsQuantity,
      packingSelected?.maxAvailableLayersQuantity,
      packingSelected?.maxAvailableTrolleysQuantity,
      getTextInputTypeSale,
    ],
  );

  const validateMinimumQuantity = useCallback(
    (_: unknown, value: string) => {
      if (!value) return Promise.resolve();
      const numValue = parseInt(value, 10);
      const minBySaleType = calculateMinBySaleType(
        selectedTypeSale,
        packingSelected ?? initialPackingSelectedState,
      );
      if (packingSelected?.minimumQuantity && numValue < minBySaleType) {
        return Promise.reject(new Error(getErrorMinMessage()));
      }
      return Promise.resolve();
    },
    [selectedTypeSale, packingSelected, getErrorMinMessage],
  );

  const validateMultiple = useCallback(
    (_: unknown, value: string) => {
      if (!value || !packingSelected?.multiple) return Promise.resolve();
      const numValue = parseInt(value, 10);
      const textInputTypeSale = getTextInputTypeSale();
      const label = getLabel();
      const multipleCalc = getMultipleCalc();

      if (selectedTypeSale === PackingTypeSale.Packing) {
        const totalUnits = numValue * parseInt(label, 10);
        if (totalUnits % packingSelected.multiple !== 0) {
          return Promise.reject(
            new Error(
              `A quantidade não é múltipla de ${multipleCalc} ${textInputTypeSale}`,
            ),
          );
        }
      } else {
        const values = label.split('x').map((x) => parseInt(x, 10));
        const totalUnits = values.reduce((a, b) => a * b, 1) * numValue;
        if (totalUnits % packingSelected.multiple !== 0) {
          return Promise.reject(
            new Error(
              `A quantidade não é múltipla de ${multipleCalc} ${textInputTypeSale}`,
            ),
          );
        }
      }
      return Promise.resolve();
    },
    [
      selectedTypeSale,
      packingSelected,
      getTextInputTypeSale,
      getLabel,
      getMultipleCalc,
    ],
  );

  const resetQuantity = useCallback(() => {
  setQuantity(null);
    form.resetFields(['quantity']);
  }, [form]);

  const handleAddToCart = async () => {
    if (!isQuantityValid) return;
    try {
      await form.validateFields(['quantity']);
      if (purchaseInfo.exceedsTrolley) {
        setPendingAction('addToCart');
        setShowTrolleyModal(true);
        return;
      }
      await onAddToCart(parsedQuantity, selectedTypeSale);
      resetQuantity();
    } catch {
      // Validation failed - Não deve impactar no processo
    }
  };

  const handleBuyNow = async () => {
    if (!isQuantityValid) return;
    try {
      await form.validateFields(['quantity']);
      if (purchaseInfo.exceedsTrolley && isLkpOffer) {
        setPendingAction('buyNow');
        setShowTrolleyModal(true);
        return;
      }
      await onBuyNow(parsedQuantity, selectedTypeSale);
      resetQuantity();
    } catch {
      // Validation failed - Não deve impactar no processo
    }
  };

  const handleTrolleyConfirm = async () => {
    setShowTrolleyModal(false);
    if (pendingAction === 'addToCart') {
      await onAddToCart(parsedQuantity, selectedTypeSale);
    } else {
      await onBuyNow(parsedQuantity, selectedTypeSale);
    }
    resetQuantity();
  };

  const calculatedQuantity = useCallback(
    (packingType: PackingTypeSale) => {
      if (parsedQuantity && packingSelected) {
        if (packingType === PackingTypeSale.Packing) {
          const calculated = parsedQuantity * packingSelected.unitsPerPackage;
          return calculated;
        }
        if (packingType === PackingTypeSale.Layer) {
          const calculated =
            parsedQuantity *
            (packingSelected.packagingByLayer *
              packingSelected.unitsPerPackage);
          return calculated;
        }
        if (packingType === PackingTypeSale.Trolley) {
          const calculated =
            parsedQuantity *
            (packingSelected.packagingByLayer *
              packingSelected.layersByTrolley *
              packingSelected.unitsPerPackage);
          return calculated;
        }
      }
      return 1;
    },
    [packingSelected, parsedQuantity],
  );

  const getDefaultPrice = useCallback(
    (packingType: PackingTypeSale) => {
      let basePrice = 0;
      switch (packingType) {
        case PackingTypeSale.Layer:
          basePrice = product.layerPrice || 0;
          break;
        case PackingTypeSale.Trolley:
          basePrice = product.trolleyPrice || 0;
          break;
        default:
          basePrice = product.packagingPrice || 0;
          break;
      }
      if (basePrice > 0 && packingSelected?.additionalCost) {
        return basePrice + product.packingCost;
      }
      return basePrice;
    },
    [product, packingSelected?.additionalCost],
  );

  const getPrice = useCallback(
    (packingType: PackingTypeSale) => {
      if (!parsedQuantity || !product.specialPrice) {
        return getDefaultPrice(packingType);
      }

      const applyPackingCost = (price: number) => {
        if (price > 0 && packingSelected?.additionalCost) {
          return price + product.packingCost;
        }
        return price;
      };

      switch (packingType) {
        case PackingTypeSale.Layer:
          return calculatedQuantity(PackingTypeSale.Layer) >=
            product.minimumQuantity
            ? applyPackingCost(product.specialPrice)
            : getDefaultPrice(packingType);
        case PackingTypeSale.Trolley:
          return calculatedQuantity(PackingTypeSale.Trolley) >=
            product.minimumQuantity
            ? applyPackingCost(product.specialPrice)
            : getDefaultPrice(packingType);
        default:
          return calculatedQuantity(PackingTypeSale.Packing) >=
            product.minimumQuantity
            ? applyPackingCost(product.specialPrice)
            : getDefaultPrice(packingType);
      }
    },
    [
      parsedQuantity,
      product,
      packingSelected?.additionalCost,
      calculatedQuantity,
      getDefaultPrice,
    ],
  );

  const saleOptions: SaleOption[] = useMemo(
    () => [
      {
        label: 'Embalagem',
        value: PackingTypeSale.Packing,
        price: getPrice(PackingTypeSale.Packing),
        available: (packingSelected?.maxAvailablePackingsQuantity || 0) > 0,
      },
      {
        label: 'Camada',
        value: PackingTypeSale.Layer,
        price: getPrice(PackingTypeSale.Layer),
        available: (packingSelected?.maxAvailableLayersQuantity || 0) > 0,
      },
      {
        label: 'Carrinho',
        value: PackingTypeSale.Trolley,
        price: getPrice(PackingTypeSale.Trolley),
        available: (packingSelected?.maxAvailableTrolleysQuantity || 0) > 0,
      },
    ],
    [packingSelected, getPrice],
  );

  useEffect(() => {
    const firstAvailable = saleOptions.find(
      (option) => option.available && option.price > 0,
    );
    if (firstAvailable && firstAvailable.value !== selectedTypeSale) {
      const currentOption = saleOptions.find(
        (option) => option.value === selectedTypeSale,
      );
      if (!currentOption?.available || currentOption?.price <= 0) {
        setSelectedTypeSale(firstAvailable.value);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [saleOptions]);

  const currentSaleInfo = useMemo(() => {
    const getCurrentInfo = () => {
      switch (selectedTypeSale) {
        case PackingTypeSale.Layer:
          return {
            label: packingSelected?.maxAvailableLayersLabel || '',
            max: packingSelected?.maxAvailableLayersQuantity || 0,
          };
        case PackingTypeSale.Trolley:
          return {
            label: packingSelected?.maxAvailableTrolleysLabel || '',
            max: packingSelected?.maxAvailableTrolleysQuantity || 0,
          };
        default:
          return {
            label: packingSelected?.maxAvailablePackingsLabel || '',
            max: packingSelected?.maxAvailablePackingsQuantity || 0,
          };
      }
    };

    const info = getCurrentInfo();

    return {
      min: calculateMinBySaleType(
        selectedTypeSale,
        packingSelected ?? initialPackingSelectedState,
      ),
      max: info.max,
      availableLabel: info.label,
      shippingFee,
      inputLabel:
        saleOptions.find((opt) => opt.value === selectedTypeSale)?.label ||
        'Un',
    };
  }, [selectedTypeSale, saleOptions, shippingFee, packingSelected]);

  const { offerType } = product;

  const isLkpOffer = useMemo(
    () => offerType === 1 || offerType === 2,
    [offerType],
  );

  const voucherInfoMessage = useMemo(() => {
    if (!product.voucherId || !product.minimumUnitQuantityVoucher) return null;
    const discountType =
      product.reajustmentAmount < 0 ? 'desconto' : 'adicional';
    const type = product.voucherQuantityType;
    const qty = product.minimumUnitQuantityVoucher;
    let unitLabel: string;
    if (type === 'P') unitLabel = qty > 1 ? 'embalagens' : 'embalagem';
    else if (type === 'L') unitLabel = qty > 1 ? 'camadas' : 'camada';
    else unitLabel = qty > 1 ? 'carrinhos' : 'carrinho';
    return `*Para aplicação do ${discountType} a quantidade mínima deve ser de ${qty} ${unitLabel}. O valor ${
      product.reajustmentAmount < 0 ? 'do desconto' : 'adicional'
    } será aplicado sobre o preço do produto`;
  }, [
    product.voucherId,
    product.minimumUnitQuantityVoucher,
    product.reajustmentAmount,
    product.voucherQuantityType,
  ]);

  const purchaseInfo = usePurchaseInfoDisplay(
    selectedTypeSale,
    packingSelected,
    parsedQuantity,
    shippingFee,
  );

  const isBuyButtonDisabled = disabled || disableBuyButton || !isQuantityValid;
  const isAddToCartDisabled = disabled || !isQuantityValid;

  const buyButtonTooltip = useMemo(() => {
    if (disableBuyButton) return 'Comprar agora não disponível para ofertas ENP';
    if (!isQuantityValid) return INVALID_QUANTITY_MESSAGE;
    return '';
  }, [disableBuyButton, isQuantityValid]);

  const trolleyModalTotalPrice = useMemo(() => {
    const unitPrice = saleOptions.find((opt) => opt.value === selectedTypeSale)?.price ?? 0;
    return multiplyMoney(unitPrice, purchaseInfo.totalUnits);
  }, [purchaseInfo.totalUnits, saleOptions, selectedTypeSale]);

  return (
    <S.ActionsContainer className="product-card-actions">
      <S.ControlsSection className="controls-section">
        <S.SaleTypeGrid className="sale-type-grid">
          {saleOptions.map((option) => {
            const isChecked = selectedTypeSale === option.value;
            const hasPrice = option.price > 0;
            const isOptionDisabled = !option.available || !hasPrice || disabled;
            return (
              <>
                <S.PackingButton
                  key={option.value}
                  onClick={() => {
                    if (!isOptionDisabled) {
                      setSelectedTypeSale(option.value);
                      form.resetFields(['quantity']);
                    setQuantity(null);
                    }
                  }}
                  style={{
                    opacity: isOptionDisabled ? 0.5 : 1,
                    cursor: isOptionDisabled ? 'not-allowed' : 'pointer',
                  }}
                >
                  <S.Radio
                    checked={isChecked}
                    value={option.value}
                    disabled={isOptionDisabled}
                    readOnly
                    className="sale-type-label"
                  >
                    {option.label}
                  </S.Radio>
                  <S.Monetary isChecked={isChecked} className="sale-type-price">
                    {hasPrice ? `R$ ${formatDecimal(option.price)}` : '-'}
                  </S.Monetary>
                </S.PackingButton>
                {option.value !== PackingTypeSale.Trolley && (
                  <S.Divider className="sale-type-divider" />
                )}
              </>
            );
          })}
        </S.SaleTypeGrid>

        <Form form={form} component={false}>
          <Form.Item
            name="quantity"
            style={{ margin: 0, width: '100%' }}
            validateTrigger="onSubmit"
            rules={[
              { required: true, message: 'A quantidade é obrigatória.' },
              { validator: validateMaximumQuantity },
              { validator: validateMinimumQuantity },
              { validator: validateMultiple },
            ]}
          >
          <InputNumber
            value={quantity ?? undefined}
            onChange={(value) => {
              setQuantity(value);
              form.setFieldsValue({ quantity: value })
            }}
            controls={false}
            addonAfter={currentSaleInfo.inputLabel}
            style={{ width: 300 }}
            disabled={disabled}
          />
          </Form.Item>
        </Form>

        <S.InfoRowContainer>
          <S.InfoRow>
            <span>{purchaseInfo.qtdMinimaText}</span>
            <S.InfoDivider>|</S.InfoDivider>
            <span>Total disponível: {currentSaleInfo.max}</span>
            <S.ShippingFee>{purchaseInfo.shippingFeeText}</S.ShippingFee>
          </S.InfoRow>
          {purchaseInfo.compositionLine && (
            <S.CompositionLine>{purchaseInfo.compositionLine}</S.CompositionLine>
          )}
          {purchaseInfo.totalPedidoText && (
            <S.TotalPedidoLine>{purchaseInfo.totalPedidoText}</S.TotalPedidoLine>
          )}
          {voucherInfoMessage && (
            <S.VoucherInfoRow>{voucherInfoMessage}</S.VoucherInfoRow>
          )}
          {!!product.minimumQuantity && product.minimumQuantity > 0 && (
            <InfoDirected
              directedMessage={
                product.minimumQuantity === 1
                  ? `Para aplicação do valor promocional, a compra mínima deverá ser de 1 unidade.`
                  : `Para aplicação do valor promocional, a compra mínima deverá ser de ${product.minimumQuantity} unidades.`
              }
            />
          )}
        </S.InfoRowContainer>
      </S.ControlsSection>

      <S.ButtonsSection className="buttons-section">
        <S.DetailsLinkButton
          type="link"
          onClick={onShowDetails}
          disabled={disabled}
        >
          <span className="mobile-text">
            {isLkpOffer ? 'Detalhes | GFPs' : 'Detalhes'}
          </span>
          <span className="desktop-text">
            {isLkpOffer ? 'Detalhes | Comprar por GFP' : 'Detalhes'}
          </span>
          <RightOutlined />
        </S.DetailsLinkButton>

        <S.ButtonsRow>
          <Tooltip title={buyButtonTooltip} style={{ flex: 1 }}>
            <S.ButtonTooltipWrapper isDisabled={isBuyButtonDisabled}>
              <S.BuyButton
                type="primary"
                onClick={handleBuyNow}
                disabled={isBuyButtonDisabled}
              >
                Comprar agora
                <DollarCircleOutlined />
              </S.BuyButton>
            </S.ButtonTooltipWrapper>
          </Tooltip>
          <Tooltip title={isQuantityValid ? '' : INVALID_QUANTITY_MESSAGE}>
            <S.ButtonTooltipWrapper isDisabled={isAddToCartDisabled}>
              <CartButton
                onClick={handleAddToCart}
                disabled={isAddToCartDisabled}
              >
                {showCartText && (
                  <S.CartText className="cart-text">
                    Adicionar ao Carrinho
                  </S.CartText>
                )}
                <ShoppingCartOutlined />
              </CartButton>
            </S.ButtonTooltipWrapper>
          </Tooltip>
          {onComposition && showCompositionButton && (
            <CartButton onClick={onComposition} disabled={disabled}>
              <ApartmentIcon />
            </CartButton>
          )}
        </S.ButtonsRow>
      </S.ButtonsSection>

      <TrolleyLimitModal
        visible={showTrolleyModal}
        totalUnits={purchaseInfo.totalUnits}
        quantityOrdered={parsedQuantity}
        selectedTypeSale={selectedTypeSale}
        unitsPerType={purchaseInfo.unitsPerType}
        totalPrice={trolleyModalTotalPrice}
        shippingFeeTotal={
          hasShippingFee ? purchaseInfo.totalUnits * shippingFee : undefined
        }
        onConfirm={handleTrolleyConfirm}
        onCancel={() => setShowTrolleyModal(false)}
      />
    </S.ActionsContainer>
  );
}
